import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { downloadGradeReport, downloadGradeClassReport } from '../lib/downloadGradeReport';
import ReportPeriodSelect from './ReportPeriodSelect';

export default function GradeReportDownload() {
  const [students, setStudents] = useState([]);
  const [years, setYears] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [year, setYear] = useState('');
  const [search, setSearch] = useState('');
  const [grade, setGrade] = useState('');
  const [period, setPeriod] = useState('10');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const load = async () => {
    setLoading(true); setError('');
    try {
      const [s, y] = await Promise.all([axios.get('/students'), axios.get('/academic-years')]);
      setStudents(s.data); setYears(y.data);
      const current = y.data.find(item => item.is_current) || y.data.find(item => item.is_active);
      setYear(current?.year_name || '');
    } catch (_) { setError('No se pudieron cargar los estudiantes y años escolares.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const name = s => [s.first_name, s.middle_name, s.last_name_paternal, s.last_name_maternal].filter(Boolean).join(' ');
  const grades = [...new Set(students.filter(s => s.is_active !== false && !s.is_withdrawn).map(s => s.assigned_grade).filter(Boolean))].sort((a,b) => a.localeCompare(b, 'es', { numeric: true }));
  const gradeCount = students.filter(s => s.assigned_grade === grade && s.is_active !== false && !s.is_withdrawn).length;
  const filtered = students.filter(s => (!grade || s.assigned_grade === grade) && `${name(s)} ${s.student_number || ''} ${s.assigned_grade || ''}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  const selected = students.find(s => s.id === studentId);
  return <Card>
    <CardHeader><CardTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-blue-600" />Descargar boleta de notas</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <p className="text-sm text-slate-600">Descarga una boleta individual o un solo PDF con las boletas de todos los estudiantes activos de un grado.</p>
      {error ? <div role="alert"><p>{error}</p><Button variant="outline" onClick={load}>Reintentar</Button></div> : <>
        <div><Label htmlFor="report-grade">Grado</Label><select id="report-grade" className="w-full border rounded-md p-2 bg-white" value={grade} onChange={e => { setGrade(e.target.value); setStudentId(''); }} disabled={loading || downloading}>
          <option value="">Selecciona un grado</option>{grades.map(g => <option key={g} value={g}>{g}</option>)}
        </select></div>
        <div><Label htmlFor="report-student-search">Buscar estudiante</Label><Input id="report-student-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Nombre, número o grado" disabled={loading} /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label htmlFor="report-student">Estudiante</Label><select id="report-student" className="w-full border rounded-md p-2 bg-white" value={studentId} onChange={e => setStudentId(e.target.value)} disabled={loading || downloading}>
            <option value="">{loading ? 'Cargando…' : 'Selecciona un estudiante'}</option>
            {selected && !filtered.includes(selected) && <option value={selected.id}>{name(selected)} · {selected.assigned_grade}</option>}
            {filtered.map(s => <option key={s.id} value={s.id}>{name(s)} · {s.assigned_grade} {s.student_number ? `· ${s.student_number}` : ''}</option>)}
          </select></div>
          <div><Label htmlFor="report-academic-year">Año escolar</Label><select id="report-academic-year" className="w-full border rounded-md p-2 bg-white" value={year} onChange={e => setYear(e.target.value)} disabled={loading || downloading}>
            <option value="">Año escolar actual</option>{years.map(y => <option key={y.id || y.year_name} value={y.year_name}>{y.year_name}</option>)}
          </select></div>
        </div>
        <ReportPeriodSelect id="grade-download-period" value={period} onChange={setPeriod} disabled={loading || downloading} />
        {!loading && !students.length && <p>No hay estudiantes disponibles.</p>}
        <div className="flex flex-wrap gap-3"><Button disabled={loading || downloading || !selected} onClick={async () => {
          setDownloading(true);
          try { await downloadGradeReport(selected, { academicYear: year, period }); toast.success('Boleta descargada'); }
          catch (error) { toast.error(error.message); }
          finally { setDownloading(false); }
        }}><Download className="w-4 h-4 mr-2" />{downloading ? 'Preparando PDF…' : 'Descargar boleta individual'}</Button>
        <Button variant="outline" disabled={loading || downloading || !grade || !gradeCount} onClick={async () => {
          setDownloading(true);
          try { await downloadGradeClassReport(grade, year, period); toast.success('Boletas del grado descargadas'); }
          catch (error) { toast.error(error.message); }
          finally { setDownloading(false); }
        }}><Download className="w-4 h-4 mr-2" />{downloading ? 'Preparando PDF…' : `Descargar grado${grade ? ` ${grade} (${gradeCount})` : ''}`}</Button></div>
      </>}
    </CardContent>
  </Card>;
}
